import React from 'react';
import styled from '@emotion/styled';

import { CategoryTypeString } from '@diritto/common/content/constants';
import { ContentPublicRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { ProfileLitePublicRelayDto } from '@diritto/common/relay/dtos/profile-lite-relay.dtos';
import { Color } from '@diritto/common/style/color';
import { FontWeight, Typography } from '@diritto/common/style/typography';

import { ContentDescriptionRowCount } from 'constants/content';
import { isPC } from 'constants/layout';
import { SectionContentKeywords } from 'app-components/home/sections/components/SectionContentKeywords';
import { getTextLineStyle } from 'styles/common';
import { DotDivider } from 'ui-components/common/DotDivider';
import { Link } from 'ui-components/common/Link';
import { IconCount, IconCountType } from 'ui-components/contents/IconCount';
import { ContentCover } from 'ui-components/contents/ContentCover';
import { ProfileName } from 'app-components/contents/ProfileName';

interface SectionContentItemProps {
  content: ContentPublicRelayDto;
  profile: ProfileLitePublicRelayDto;
  showKeyword?: boolean;
  showCategory?: boolean;
  showDescription?: boolean;
  showViewCount?: boolean;
  onClick?: () => void;
}

export const SectionContentItem = ({
  content,
  profile,
  showKeyword = false,
  showCategory = false,
  showDescription = false,
  showViewCount = false,
  onClick,
}: SectionContentItemProps) => (
  <StyledContentWrapper onClick={onClick}>
    <Link href={`/contents/${content.id}`}>
      <ContentCover content={content} size={100} />
    </Link>
    <StyledContentMetaContainer>
      <StyledMeta>
        <Link href={`/contents/${content.id}`}>
          <StyledTitle>{content.title}</StyledTitle>
        </Link>
      </StyledMeta>
      <StyledMeta>
        <ProfileName profile={profile} />
        {showCategory ? (
          <>
            <DotDivider />
            <div>{CategoryTypeString[content.categoryType]}</div>
          </>
        ) : null}
        <DotDivider />
        <div>{`${content.counts.episodeCount}화`}</div>
      </StyledMeta>
      {showKeyword && (
        <StyledMeta>
          <SectionContentKeywords keywords={content.keywords} />
        </StyledMeta>
      )}
      {showViewCount && (
        <StyledMeta>
          <IconCount count={content.counts.viewCount || 0} type={IconCountType.View} />
        </StyledMeta>
      )}
      {showDescription && (
        <StyledMeta>
          <StyledDescription>{content.description}</StyledDescription>
        </StyledMeta>
      )}
    </StyledContentMetaContainer>
  </StyledContentWrapper>
);

const StyledContentWrapper = styled.div`
  width: 300px;
  display: flex;
  align-items: center;
  flex-direction: row;
  gap: 16px;
  @media (${isPC}) {
    width: 338px;
  }
`;

const StyledContentMetaContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  flex-shrink: 1;
  gap: 4px;
`;

const StyledTitle = styled.p`
  color: ${Color.Black};
  font-weight: ${FontWeight.Bold};
  ${Typography.XSmall};
  ${getTextLineStyle(2)};
`;

const StyledMeta = styled.div`
  display: flex;
  flex-direction: row;
  line-height: initial;
  gap: 4px;

  font-weight: ${FontWeight.Medium};
  ${Typography.XXXSmall};
  color: ${Color.Gray600};
`;

const StyledDescription = styled.div`
  ${getTextLineStyle(ContentDescriptionRowCount)}
  font-weight: ${FontWeight.Medium};
`;
